// The index of titles PoKu already has a file for — the admin hosted-file map
// (GET /api/hosted/list).
//
// Why search needs it: TMDB orders search results by its own popularity, and
// the titles this site can actually play are frequently the ones TMDB buries. A
// 2003 Tamil film with 47 votes sits ~100 pages deep in a "boys" search, so a
// user who typed the name of a film sitting in the library saw everything
// except that film. Matching the library against the query puts them back.
//
// The list is small (one line per mapped title) and cached for a few minutes,
// so typing "b-o-y-s" costs at most one request. A failure is never fatal:
// search simply runs without the library boost.
import { apiUrl } from "../config";

const INDEX_TTL_MS = 5 * 60 * 1000;

let cache = { at: 0, entries: [], inflight: null };

/** Test seam / "the admin just mapped something" reset. */
export function resetLibraryIndex() {
  cache = { at: 0, entries: [], inflight: null };
}

// Mapping keys are "movie:<tmdbId>" for films and "tv:<tmdbId>:<season>:<episode>"
// for episodes — one key per episode, one entry per unknown title.
function entryOf(key, record) {
  const parts = String(key).split(":");
  const tmdbId = Number(parts[1]);
  if (!tmdbId) return null;
  return {
    key: String(key),
    tmdbId,
    contentType: parts[0] === "tv" ? "TV_SHOW" : "MOVIE",
    title: String(record?.title || "").trim(),
    season: parts[2] !== undefined ? Number(parts[2]) : null,
    episode: parts[3] !== undefined ? Number(parts[3]) : null,
  };
}

/** Every distinct title the library holds a file for. Never throws. */
export async function fetchLibraryIndex() {
  const fresh = Date.now() - cache.at < INDEX_TTL_MS && cache.at > 0;
  if (fresh) return cache.entries;
  if (cache.inflight) return cache.inflight;
  cache.inflight = (async () => {
    try {
      const res = await fetch(apiUrl("/api/hosted/list"), { headers: { accept: "application/json" } });
      if (!res.ok) return cache.entries;
      const data = await res.json();
      const raw = data?.mappings;
      const list = Array.isArray(raw) ? raw : Object.values(raw || {});
      const seen = new Set();
      const entries = [];
      for (const record of list) {
        const entry = entryOf(record?.key, record);
        if (!entry || !entry.title) continue;
        const dedupeKey = `${entry.contentType}:${entry.tmdbId}`;
        if (seen.has(dedupeKey)) continue;
        seen.add(dedupeKey);
        entries.push(entry);
      }
      cache = { at: Date.now(), entries, inflight: null };
      return entries;
    } catch {
      // Offline / old deploy without the route: keep whatever we had (usually
      // nothing) and let search run on TMDB alone.
      cache.inflight = null;
      return cache.entries;
    }
  })();
  return cache.inflight;
}
